import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Bell, X } from 'lucide-react';
import { notificationsAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Short two-tone chime via Web Audio — no asset file needed. AudioContext
// is created lazily on first use; if the browser blocks it (no prior user
// gesture) we fail silently and the popup is just visual.
let audioCtx = null;
function playChime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    // Autoplay policy: the context starts suspended until the page has seen
    // a user gesture. resume() is a no-op when already running.
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.35);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.4);
    });
  } catch (_) {}
}

// App-shell SSE listener, mounted once in Layout. Two jobs:
//   1. Any notification for the current user → invalidate the bell queries
//      so the dropdown/badge update in real time instead of waiting for the
//      next poll.
//   2. If the SSE payload carries `popup: true` (opt-in per automation rule
//      via the notify_user "Also show on-screen popup" checkbox) → render an
//      in-app toast while the user is looking at the app, or a browser
//      desktop notification while they're away.
//
// Away handling: toasts must NOT be created while the tab is hidden —
// react-hot-toast measures card heights on mount, hidden tabs report 0, and
// the cards come back stacked on top of each other. Worse, Chrome freezes
// long-hidden tabs entirely: SSE events are never processed (or the
// connection dies and events are lost — SSE has no replay here). So instead
// of trusting an in-memory queue, the popups are re-read from the API on
// return: notifications persist `data.popup`, and when the user comes back
// we fetch what arrived since they left. One lead → its own sticky card,
// several → a single "N new leads while you were away" summary.
//
// SSE events are org-scoped but notifications are user-scoped, so the
// userId filter here is what keeps one user's popup off a teammate's screen.
const useNotificationStream = (user) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Ref so the EventSource effect doesn't tear down/reopen on every auth
  // context re-render — only token presence controls the connection.
  const userIdRef = useRef(null);
  userIdRef.current = user?._id || user?.id || null;

  // Which popup notifications have already been shown on screen (any
  // channel) so the catch-up fetch never duplicates them, plus an in-flight
  // guard so visibilitychange + focus firing together can't double-render.
  const shownIdsRef = useRef(new Set());
  const flushingRef = useRef(false);

  // The "enable desktop notifications" helper is shown at most once per
  // session (ref) and never again once dismissed/denied (localStorage).
  const permPromptShownRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let es = null;

    const showLeadToast = (item, { sticky }) => {
      const goToLead = () => {
        if (item.leadId) navigate(`/leads/${item.leadId}`);
      };
      toast.custom(
        (t) => (
          <div
            onClick={() => {
              toast.dismiss(t.id);
              goToLead();
            }}
            className={`${t.visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-3 scale-95'}
              transition-all duration-300 max-w-md w-full bg-white shadow-2xl rounded-xl
              ring-1 ring-black/10 border-l-4 border-blue-600 pointer-events-auto
              flex items-start gap-3 p-4 cursor-pointer`}
          >
            <div className="relative flex-shrink-0">
              <div className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center">
                <Bell size={20} className="text-white" />
              </div>
              <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-gray-900">{item.title || 'Notification'}</p>
              {item.message && <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{item.message}</p>}
              {item.leadId && (
                <p className="text-xs font-semibold text-blue-600 mt-1.5">Click to open lead →</p>
              )}
            </div>
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                toast.dismiss(t.id);
              }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        ),
        { duration: sticky ? Infinity : 15000, position: 'top-right' }
      );
    };

    const maybeShowPermissionPrompt = () => {
      if (permPromptShownRef.current) return;
      if (typeof Notification === 'undefined') return;
      if (Notification.permission === 'granted') return;
      if (localStorage.getItem('desktopNotifPromptDismissed') === '1') return;
      permPromptShownRef.current = true;
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
              transition-all duration-300 max-w-md w-full bg-amber-50 shadow-xl rounded-xl
              ring-1 ring-amber-200 border-l-4 border-amber-500 pointer-events-auto
              flex items-start gap-3 p-4`}
          >
            <div className="flex-1 min-w-0 text-sm text-amber-900">
              Desktop notifications are off — you'll only see new-lead popups inside the app.
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  Notification.requestPermission().then((res) => {
                    if (res === 'granted') {
                      toast.success('Desktop notifications enabled');
                    } else {
                      localStorage.setItem('desktopNotifPromptDismissed', '1');
                    }
                  });
                }}
                className="block mt-1.5 font-semibold underline text-amber-900"
              >
                Enable desktop notifications →
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem('desktopNotifPromptDismissed', '1');
                toast.dismiss(t.id);
              }}
              className="flex-shrink-0 text-amber-500 hover:text-amber-700"
            >
              <X size={16} />
            </button>
          </div>
        ),
        { duration: Infinity, position: 'top-right' }
      );
    };

    // Show whatever the user hasn't seen yet, straight from the API —
    // survives frozen tabs, dead SSE connections, and HMR swaps. No
    // "when did you leave" bookkeeping (blur/visibility ordering proved
    // unreliable): anything unread, popup-flagged, recent, and not already
    // shown on this screen is fair game.
    // Only ever surface popups that arrived during THIS page session — after
    // a refresh the slate is clean (the bell still holds older unread ones).
    const sessionStart = Date.now();
    const MISSED_WINDOW_MS = 30 * 60 * 1000;
    const flushMissed = async () => {
      if (flushingRef.current) return;
      flushingRef.current = true;
      let list = [];
      try {
        const res = await notificationsAPI.getNotifications();
        list = res?.data?.notifications || [];
      } catch (_) {
        flushingRef.current = false;
        return;
      }
      const cutoff = Math.max(sessionStart, Date.now() - MISSED_WINDOW_MS);
      const missed = list.filter(
        (n) =>
          !n.read &&
          n?.data?.popup &&
          new Date(n.createdAt).getTime() >= cutoff &&
          !shownIdsRef.current.has(n._id)
      );
      flushingRef.current = false;
      if (missed.length === 0) return;
      missed.forEach((n) => {
        shownIdsRef.current.add(n._id);
        showLeadToast(
          { id: n._id, title: n.title, message: n.message, leadId: n.data?.leadId },
          { sticky: true }
        );
      });
      maybeShowPermissionPrompt();
    };

    const handleMessage = (e) => {
      let data;
      try {
        data = JSON.parse(e.data);
      } catch (_) {
        return;
      }
      if (data.type !== 'notification' || !data.notification) return;

      const n = data.notification;
      const me = userIdRef.current;
      if (!me || String(n.userId) !== String(me)) return;

      queryClient.invalidateQueries('notifications');
      queryClient.invalidateQueries('unread-notifications');

      if (!data.popup) return;

      const item = {
        id: n._id,
        title: n.title,
        message: n.message,
        leadId: n.data?.leadId,
      };

      playChime();

      // Tab visible (even if the window isn't focused — e.g. user is typing
      // in another app with Chrome beside it): render the toast right away.
      // Visible tabs measure toast heights correctly. Sticky when unfocused
      // so it's still there when the user looks back.
      if (!document.hidden) {
        shownIdsRef.current.add(item.id);
        showLeadToast(item, { sticky: !document.hasFocus() });
        return;
      }

      // Tab hidden (user is on another tab / window minimized).
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // `tag` dedupes the same event across multiple open tabs;
        // requireInteraction keeps it on screen until dismissed.
        shownIdsRef.current.add(item.id);
        const desktop = new Notification(n.title || 'Notification', {
          body: n.message || '',
          tag: n._id,
          requireInteraction: true,
        });
        desktop.onclick = () => {
          window.focus();
          if (item.leadId) navigate(`/leads/${item.leadId}`);
          desktop.close();
        };
        return;
      }

      // Hidden tab and no desktop channel: only the chime fires now;
      // flushMissed re-reads this notification from the API on return.
      // No toast here — hidden-tab toasts break react-hot-toast's height
      // measurement, and a frozen tab wouldn't even run this handler.
    };

    const openStream = () => {
      if (es) es.close();
      es = new EventSource(`${API_BASE_URL}/events?token=${encodeURIComponent(token)}`);
      es.onmessage = handleMessage;
    };

    openStream();

    // On every return to the tab: self-heal the stream (EventSource gives up
    // permanently on non-network failures, and frozen tabs can leave it
    // closed), then show what was missed. Deliberately NOT run on mount —
    // a page refresh starts clean; the bell already holds anything unseen.
    const onReturn = () => {
      if (es && es.readyState === EventSource.CLOSED) openStream();
      if (!document.hidden) flushMissed();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);

    // Belt-and-braces heartbeat: frozen tabs can miss the visibility/focus
    // events entirely (Chrome defers them). This pulse watches the
    // hidden→visible transition itself and only then re-checks the API, so
    // it costs one request per return, not one per tick.
    let wasHidden = document.hidden;
    const pulse = setInterval(() => {
      if (document.hidden) {
        wasHidden = true;
        return;
      }
      if (wasHidden) {
        wasHidden = false;
        onReturn();
      }
    }, 4000);

    return () => {
      clearInterval(pulse);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      if (es) es.close();
    };
  }, [queryClient, navigate]);
};

export default useNotificationStream;
